import { useState, useEffect } from 'react';
import apiClient from '../utils/api';
import PaginationBar from '../components/shared/PaginationBar';
import PageRefinedHero from '../components/layout/PageRefinedHero';

const formatInsightDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const InsightCard = ({ article, onRead }) => (
  <article className="insight-card" onClick={() => onRead(article)} role="button" tabIndex={0}
    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onRead(article); } }}>
    <div
      className="insight-card-image"
      style={{ backgroundImage: article.image ? `url(${article.image})` : undefined }}
    />
    <div className="insight-card-body">
      <div className="insight-card-tag">{article.tag || "News"}</div>
      <h4 className="insight-card-title">{article.title}</h4>
      <p style={{ color: "var(--text-light)", fontSize: "0.9rem", marginBottom: "1rem" }}>{article.excerpt}</p>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.8rem", color: "var(--text-light)" }}>
        <span><i className="far fa-calendar"></i> {formatInsightDate(article.publishedAt)}</span>
        <span style={{ color: "var(--primary)", fontWeight: 600 }}>Read more →</span>
      </div>
    </div>
  </article>
);

const NewsPage = ({ setPage }) => {
  const [articles, setArticles] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTag, setActiveTag] = useState("All");
  const [page, setPageNum] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 12, total: 0, totalPages: 1 });
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadList = async (opts = {}) => {
    setLoading(true);
    try {
      const res = await apiClient.getInsights({
        search: opts.search ?? searchQuery,
        tag: opts.tag ?? activeTag,
        page: opts.page ?? page,
        pageSize: 12
      });
      setArticles(Array.isArray(res.data) ? res.data : []);
      if (res.pagination) setPagination(res.pagination);
      if (Array.isArray(res.tags) && res.tags.length) setTags(res.tags);
    } catch (err) {
      setArticles([]);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadList({ page: 1 }); }, []);

  const runSearch = (e) => {
    e?.preventDefault();
    setSearchQuery(searchInput.trim());
    setPageNum(1);
    loadList({ search: searchInput.trim(), page: 1 });
  };

  const selectTag = (tag) => {
    setActiveTag(tag);
    setPageNum(1);
    loadList({ tag, page: 1 });
  };

  const openArticle = async (article) => {
    setSelected(article);
    setDetailLoading(true);
    try {
      const res = await apiClient.getInsight(article.id);
      if (res.data) setSelected(res.data);
    } catch (_) { /* keep list row */ }
    finally { setDetailLoading(false); }
  };

  if (selected) {
    return (
      <div className="container" style={{ paddingTop: "2rem", maxWidth: "900px" }}>
        <button type="button" className="btn btn-ghost btn-sm" style={{ marginBottom: "1rem" }} onClick={() => setSelected(null)}>
          <i className="fas fa-arrow-left"></i> Back to Insights
        </button>
        {detailLoading && <p style={{ color: "var(--text-light)" }}>Loading…</p>}
        <div
          className="insight-detail-hero"
          style={{ backgroundImage: selected.image ? `url(${selected.image})` : undefined }}
        />
        <div style={{ marginBottom: "1rem" }}>
          <div className="insight-card-tag">{selected.tag}</div>
          <h1 style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: "2rem", margin: "0.35rem 0" }}>{selected.title}</h1>
          <p style={{ color: "var(--text-light)", fontSize: "0.9rem" }}>
            <i className="far fa-calendar"></i> {formatInsightDate(selected.publishedAt)}
          </p>
        </div>
        <p style={{ fontSize: "1.05rem", color: "var(--text-light)", marginBottom: "1.5rem", lineHeight: 1.6 }}>{selected.excerpt}</p>
        <div className="insight-detail-body">{selected.body || selected.excerpt}</div>
      </div>
    );
  }

  const tagFilters = ["All", ...(tags.length ? tags : ["Platform News", "Company", "Funding", "Market Trends", "Education"])];

  return (
    <div className="coown-page coown-page--hero" data-listings-anchor>
      <PageRefinedHero
        badge="Market intelligence"
        title="CoOwn"
        titleAccent="Insights"
        subtitle="Platform news, market context, and investor education — curated for fractional real estate co-owners."
        benefits={[
          { icon: 'fas fa-newspaper', title: 'Platform updates', subtitle: 'Product news and milestones' },
          { icon: 'fas fa-chart-area', title: 'Market context', subtitle: 'Trends across key regions' },
          { icon: 'fas fa-graduation-cap', title: 'Investor guides', subtitle: 'Learn how co-ownership works' },
        ]}
      />

      <div className="coown-page-body">
        <form className="insights-toolbar" onSubmit={runSearch}>
          <div className="insights-search">
            <i className="fas fa-search" style={{ color: "var(--text-light)" }} aria-hidden="true"></i>
            <input
              type="search"
              placeholder="Search articles…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label="Search insights"
            />
          </div>
          <button type="submit" className="btn btn-primary btn-sm">Search</button>
          {searchQuery && (
            <button type="button" className="btn btn-outline btn-sm" onClick={() => {
              setSearchInput("");
              setSearchQuery("");
              setPageNum(1);
              loadList({ search: "", page: 1 });
            }}>
              Clear
            </button>
          )}
        </form>
        <div className="filters" style={{ marginBottom: "1.5rem" }}>
          {tagFilters.map((tag) => (
            <button
              key={tag}
              type="button"
              className={`filter-btn ${activeTag === tag ? "active" : ""}`}
              onClick={() => selectTag(tag)}
            >
              {tag}
            </button>
          ))}
        </div>
        {loading && <p style={{ color: "var(--text-light)" }}>Loading insights…</p>}
        {!loading && articles.length === 0 && (
          <div className="coown-page-empty">
            <i className="fas fa-newspaper coown-page-empty__icon" aria-hidden="true"></i>
            <h3>No articles found</h3>
            <p>Try another search term or category filter.</p>
          </div>
        )}
        {!loading && articles.length > 0 && (
          <>
            <div className="insights-grid">
              {articles.map((n) => (
                <InsightCard key={n.id} article={n} onRead={openArticle} />
              ))}
            </div>
            <PaginationBar
              page={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              startIndex={(pagination.page - 1) * pagination.pageSize + 1}
              endIndex={Math.min(pagination.page * pagination.pageSize, pagination.total)}
              hasPrev={pagination.page > 1}
              hasNext={pagination.page < pagination.totalPages}
              onPageChange={(p) => {
                setPageNum(p);
                loadList({ page: p });
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              itemLabel="articles"
            />
          </>
        )}
      </div>
    </div>
  );
};

export { InsightCard, formatInsightDate };
export default NewsPage;
